import React from 'react';
import { cn } from '../../lib/utils';

// Base Skeleton Component
export const Skeleton = ({ className, ...props }) => {
  return (
    <div
      className={cn(
        "animate-pulse rounded-md bg-slate-800/50",
        className
      )}
      {...props}
    />
  );
};

// Card Skeleton - for product/service cards
export const CardSkeleton = ({ className }) => (
  <div className={cn("bg-slate-900/50 border border-slate-800 rounded-xl p-6 space-y-4", className)}>
    <Skeleton className="h-40 w-full rounded-lg" />
    <Skeleton className="h-4 w-3/4" />
    <Skeleton className="h-4 w-1/2" />
    <div className="flex gap-2">
      <Skeleton className="h-8 w-20 rounded-lg" />
      <Skeleton className="h-8 w-20 rounded-lg" />
    </div>
  </div>
);

// Table Row Skeleton
export const TableRowSkeleton = ({ columns = 5 }) => (
  <tr className="border-b border-slate-800">
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <Skeleton className="h-4 w-full" />
      </td>
    ))}
  </tr>
);

// Table Skeleton
export const TableSkeleton = ({ rows = 5, columns = 5, className }) => (
  <div className={cn("bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden", className)}>
    <div className="p-4 border-b border-slate-800">
      <Skeleton className="h-6 w-40" />
    </div>
    <table className="w-full">
      <thead>
        <tr className="border-b border-slate-800 bg-slate-900/30">
          {Array.from({ length: columns }).map((_, i) => (
            <th key={i} className="px-4 py-3 text-left">
              <Skeleton className="h-4 w-20" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, i) => (
          <TableRowSkeleton key={i} columns={columns} />
        ))}
      </tbody>
    </table>
  </div>
);

// Stats Card Skeleton
export const StatsCardSkeleton = ({ className }) => (
  <div className={cn("bg-slate-900/50 border border-slate-800 rounded-xl p-6", className)}>
    <div className="flex items-center justify-between mb-4">
      <Skeleton className="h-10 w-10 rounded-lg" />
      <Skeleton className="h-4 w-16 rounded-full" />
    </div>
    <Skeleton className="h-8 w-24 mb-2" />
    <Skeleton className="h-4 w-32" />
  </div>
);

// Dashboard Grid Skeleton
export const DashboardSkeleton = ({ className }) => (
  <div className={cn("space-y-6", className)}>
    {/* Stats Row */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <StatsCardSkeleton key={i} />
      ))}
    </div>
    {/* Main Content */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <TableSkeleton rows={5} columns={4} />
      </div>
      <div className="space-y-4">
        <CardSkeleton />
        <CardSkeleton />
      </div>
    </div>
  </div>
);

// Form Skeleton
export const FormSkeleton = ({ fields = 4, className }) => (
  <div className={cn("bg-slate-900/50 border border-slate-800 rounded-xl p-6 space-y-6", className)}>
    <Skeleton className="h-6 w-48 mb-6" />
    {Array.from({ length: fields }).map((_, i) => (
      <div key={i} className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-11 w-full rounded-lg" />
      </div>
    ))}
    <div className="flex gap-3 pt-4">
      <Skeleton className="h-10 w-24 rounded-lg" />
      <Skeleton className="h-10 w-24 rounded-lg" />
    </div>
  </div>
);

// List Item Skeleton
export const ListItemSkeleton = ({ className }) => (
  <div className={cn("flex items-center gap-4 p-4 border-b border-slate-800 last:border-0", className)}>
    <Skeleton className="h-12 w-12 rounded-full flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-3 w-1/2" />
    </div>
    <Skeleton className="h-8 w-20 rounded-lg" />
  </div>
);

// List Skeleton
export const ListSkeleton = ({ items = 5, className }) => (
  <div className={cn("bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden", className)}>
    {Array.from({ length: items }).map((_, i) => (
      <ListItemSkeleton key={i} />
    ))}
  </div>
);

// Hero Section Skeleton
export const HeroSkeleton = ({ className }) => (
  <div className={cn("relative min-h-[600px] flex items-center", className)}>
    <div className="absolute inset-0 bg-slate-900/50" />
    <div className="relative z-10 max-w-7xl mx-auto px-4 py-20 w-full">
      <div className="max-w-2xl space-y-6">
        <Skeleton className="h-12 w-3/4" />
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-2/3" />
        <div className="flex gap-4 pt-4">
          <Skeleton className="h-12 w-40 rounded-lg" />
          <Skeleton className="h-12 w-40 rounded-lg" />
        </div>
      </div>
    </div>
  </div>
);

export default Skeleton;
